import { ChangeEvent, FC, ReactNode, useCallback, useMemo } from "react";
import clsx from "clsx";

import RoundedInfo from "icons/RoundedInfo";
import RoundedCheck from "icons/RoundedCheck";

import {
  StyledInput,
  StyledInputContainer,
  StyledInputLabel,
  StyledInputError,
  StyledInputAdornment,
} from "styles/components/inputs/Input";

export interface InputProps {
  id?: string;
  label?: string;
  value?: string;
  type?: "number" | "tel" | "text";
  placeholder?: string;
  defaultValue?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  error?: boolean;
  success?: boolean;
  required?: boolean;
  errorText?: string;
  suffix?: string | ReactNode;
  prefix?: string | ReactNode;
  autoFocus?: boolean;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  dataTestId?: string;
  withErrorField?: boolean;
  white?: boolean;
  width?: string;
  height?: string;
  minHeight?: string;
  borderRadius?: string;
  smallFont?: boolean;

  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const Input: FC<InputProps> = ({
  label = "Label",
  id = "initial-input-id",
  type = "text",
  value,
  defaultValue,
  placeholder = "Placeholder",
  disabled,
  error,
  success,
  className,
  inputClassName,
  required,
  errorText = "Error text",
  suffix,
  prefix,
  autoFocus,
  multiline,
  rows,
  maxRows,
  dataTestId,
  withErrorField = true,
  white,
  width,
  height,
  minHeight,
  borderRadius,
  smallFont,
  onChange,
  onBlur,
  onFocus,
}) => {
  const wrapClassName = clsx(["input-container", className]);

  const fieldClassName = clsx([
    "input",
    { input_success: success, input_white: white },
    inputClassName,
  ]);

  const errorClassName = clsx("input-error", { "input-error_visible": error });

  const renderedLabel = useMemo(
    () =>
      label ? (
        <StyledInputLabel
          htmlFor={id}
          required={required}
          data-testid={dataTestId && `${dataTestId}-label`}
        >
          {label}
        </StyledInputLabel>
      ) : null,
    [dataTestId, id, label, required]
  );

  const renderedPrefix = useMemo(
    () =>
      prefix ? (
        <StyledInputAdornment
          position="start"
          data-testid={dataTestId && `${dataTestId}-prefix`}
        >
          {prefix}
        </StyledInputAdornment>
      ) : null,

    [dataTestId, prefix]
  );

  const renderedSuffix = useMemo(() => {
    if (error) {
      return (
        <StyledInputAdornment
          position="end"
          data-testid={dataTestId && `${dataTestId}-error-icon`}
        >
          <RoundedInfo filled={false} />
        </StyledInputAdornment>
      );
    }

    if (success) {
      return (
        <StyledInputAdornment
          position="end"
          data-testid={dataTestId && `${dataTestId}-success-icon`}
        >
          <RoundedCheck />
        </StyledInputAdornment>
      );
    }

    if (suffix) {
      return (
        <StyledInputAdornment
          position="end"
          data-testid={dataTestId && `${dataTestId}-suffix`}
        >
          {suffix}
        </StyledInputAdornment>
      );
    }

    return null;
  }, [dataTestId, error, success, suffix]);

  const renderedErrorField = useMemo(
    () =>
      withErrorField ? (
        <StyledInputError
          variant="bodyM"
          fontWeight={300}
          className={errorClassName}
          data-testid={dataTestId && `${dataTestId}-error`}
        >
          {errorText}
        </StyledInputError>
      ) : null,
    [dataTestId, errorClassName, errorText, withErrorField]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value),
    [onChange]
  );

  return (
    <StyledInputContainer className={wrapClassName} data-testid={dataTestId}>
      {renderedLabel}
      <StyledInput
        id={id}
        width={width}
        height={height}
        minHeight={minHeight}
        fontSize={smallFont ? "sm" : undefined}
        borderRadius={borderRadius}
        required={required}
        disabled={disabled}
        error={error}
        type={type}
        className={fieldClassName}
        startAdornment={renderedPrefix}
        endAdornment={renderedSuffix}
        value={value}
        defaultValue={defaultValue}
        autoFocus={autoFocus}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        maxRows={maxRows}
        onChange={handleInputChange}
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid={dataTestId && `${dataTestId}-input`}
      />
      {renderedErrorField}
    </StyledInputContainer>
  );
};

export default Input;
